import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useIntl, FormattedMessage } from "react-intl";
import { Menu, MenuItem } from "@szhsin/react-menu";
import { useLongPress } from "use-long-press";
import { encodeTLV, NostrPrefix, Lists } from "@snort/system";
import { LNURL } from "@snort/shared";
import { useUserProfile } from "@snort/system-react";
import Icon from "Icons/Icon";
import Spinner from "Icons/Spinner";
import { formatShort } from "Number";
import useEventPublisher from "Feed/EventPublisher";
import { delay, normalizeReaction, unwrap } from "SnortUtils";
import { NoteCreator } from "Element/NoteCreator";
import { ReBroadcaster } from "Element/ReBroadcaster";
import Reactions from "Element/Reactions";
import SendSats from "Element/SendSats";
import { ZapsSummary } from "Element/Zap";
import { setReplyTo, setShow, reset } from "State/NoteCreator";
import { setNote as setReBroadcastNote, setShow as setReBroadcastShow, reset as resetReBroadcast, } from "State/ReBroadcast";
import useModeration from "Hooks/useModeration";
import { TranslateHost } from "Const";
import { useWallet } from "Wallet";
import useLogin from "Hooks/useLogin";
import { setBookmarked, setPinned } from "Login";
import { useInteractionCache } from "Hooks/useInteractionCache";
import { ZapPoolController } from "ZapPoolController";
import { System } from "index";
import messages from "./messages";
let isZapperBusy = false;
const barrierZapper = async (then) => {
    while (isZapperBusy) {
        await delay(100);
    }
    isZapperBusy = true;
    try {
        return await then();
    }
    finally {
        isZapperBusy = false;
    }
};
export default function NoteFooter(props) {
    const { ev, showReactions, setShowReactions, positive, negative, reposts, zaps } = props;
    const dispatch = useDispatch();
    const { formatMessage } = useIntl();
    const login = useLogin();
    const { pinned, bookmarked, publicKey, preferences: prefs, relays } = login;
    const { mute, block } = useModeration();
    const author = useUserProfile(System, ev.pubkey);
    const interactionCache = useInteractionCache(publicKey, ev.id);
    const publisher = useEventPublisher();
    const showNoteCreatorModal = useSelector((s) => s.noteCreator.show);
    const showReBroadcastModal = useSelector((s) => s.reBroadcast.show);
    const reBroadcastNote = useSelector((s) => s.reBroadcast.note);
    const replyTo = useSelector((s) => s.noteCreator.replyTo);
    const willRenderNoteCreator = showNoteCreatorModal && replyTo?.id === ev.id;
    const willRenderReBroadcast = showReBroadcastModal && reBroadcastNote && reBroadcastNote?.id === ev.id;
    const [tip, setTip] = useState(false);
    const [zapping, setZapping] = useState(false);
    const walletState = useWallet();
    const wallet = walletState.wallet;
    const isMine = ev.pubkey === publicKey;
    const lang = window.navigator.language;
    const langNames = new Intl.DisplayNames([...window.navigator.languages], {
        type: "language",
    });
    const zapTotal = zaps.reduce((acc, z) => acc + z.amount, 0);
    const didZap = interactionCache.data.zapped || zaps.some(a => a.sender === publicKey);
    const longPress = useLongPress(e => {
        e.stopPropagation();
        setTip(true);
    }, {
        captureEvent: true,
    });
    function hasReacted(emoji) {
        return (interactionCache.data.reacted ||
            positive?.some(({ pubkey, content }) => normalizeReaction(content) === emoji && pubkey === publicKey));
    }
    function hasReposted() {
        return interactionCache.data.reposted || reposts.some(a => a.pubkey === publicKey);
    }
    async function react(content) {
        if (!hasReacted(content) && publisher) {
            const evLike = await publisher.react(ev, content);
            System.BroadcastEvent(evLike);
            await interactionCache.react();
        }
    }
    async function deleteEvent() {
        if (window.confirm(formatMessage(messages.ConfirmDeletion, { id: ev.id.substring(0, 8) })) && publisher) {
            const evDelete = await publisher.delete(ev.id);
            System.BroadcastEvent(evDelete);
        }
    }
    async function repost() {
        if (!hasReposted() && publisher) {
            if (!prefs.confirmReposts || window.confirm(formatMessage(messages.ConfirmRepost, { id: ev.id }))) {
                const evRepost = await publisher.repost(ev);
                System.BroadcastEvent(evRepost);
                await interactionCache.repost();
            }
        }
    }
    function getLNURL() {
        return ev.tags.find(a => a[0] === "zap")?.[1] || author?.lud16 || author?.lud06;
    }
    function getTargetName() {
        const zapTarget = ev.tags.find(a => a[0] === "zap")?.[1];
        if (zapTarget) {
            return new LNURL(zapTarget).name;
        }
        else {
            return author?.display_name || author?.name;
        }
    }
    async function fastZap(e) {
        if (zapping || e?.isPropagationStopped())
            return;
        const lnurl = getLNURL();
        if (wallet?.isReady() && lnurl) {
            setZapping(true);
            try {
                await fastZapInner(lnurl, prefs.defaultZapAmount, ev.pubkey, ev.id);
            }
            catch (e) {
                console.warn("Fast zap failed", e);
                if (!(e instanceof Error) || e.message !== "User rejected") {
                    setTip(true);
                }
            }
            finally {
                setZapping(false);
            }
        }
        else {
            setTip(true);
        }
    }
    async function fastZapInner(lnurl, amount, key, id) {
        // only allow 1 invoice req/payment at a time to avoid hitting rate limits
        await barrierZapper(async () => {
            const handler = new LNURL(lnurl);
            await handler.load();
            const zr = Object.keys(relays.item);
            const zap = handler.canZap && publisher ? await publisher.zap(amount * 1000, key, zr, id) : undefined;
            const invoice = await handler.getInvoice(amount, undefined, zap);
            await wallet?.payInvoice(unwrap(invoice.pr));
            ZapPoolController.allocate(amount);
            await interactionCache.zap();
        });
    }
    useEffect(() => {
        if (prefs.autoZap && !didZap && !isMine && !zapping) {
            const lnurl = getLNURL();
            if (wallet?.isReady() && lnurl) {
                setZapping(true);
                queueMicrotask(async () => {
                    try {
                        await fastZapInner(lnurl, prefs.defaultZapAmount, ev.pubkey, ev.id);
                    }
                    catch {
                        // ignored
                    }
                    finally {
                        setZapping(false);
                    }
                });
            }
        }
    }, [prefs.autoZap, author, zapping]);
    function tipButton() {
        const service = getLNURL();
        if (service) {
            return (_jsx(_Fragment, { children: _jsxs("div", { className: `reaction-pill ${didZap ? "reacted" : ""}`, ...longPress(), onClick: e => fastZap(e), children: [zapping ? _jsx(Spinner, {}) : wallet?.isReady() ? _jsx(Icon, { name: "zapFast" }) : _jsx(Icon, { name: "zap" }), zapTotal > 0 && _jsx("div", { className: "reaction-pill-number", children: formatShort(zapTotal) })] }) }));
        }
        return null;
    }
    function repostIcon() {
        return (_jsxs("div", { className: `reaction-pill ${hasReposted() ? "reacted" : ""}`, onClick: () => repost(), children: [_jsx(Icon, { name: "repost", size: 17 }), reposts.length > 0 && _jsx("div", { className: "reaction-pill-number", children: formatShort(reposts.length) })] }));
    }
    function reactionIcons() {
        if (!prefs.enableReactions) {
            return null;
        }
        return (_jsx(_Fragment, { children: _jsxs("div", { className: `reaction-pill ${hasReacted("+") ? "reacted" : ""} `, onClick: () => react(prefs.reactionEmoji), children: [_jsx(Icon, { name: "heart" }), _jsx("div", { className: "reaction-pill-number", children: formatShort(positive.length) })] }) }));
    }
    async function share() {
        const link = encodeTLV(NostrPrefix.Event, ev.id, ev.relays);
        const url = `${window.location.protocol}//${window.location.host}/e/${link}`;
        if ("share" in window.navigator) {
            await window.navigator.share({
                title: "Snort",
                url: url,
            });
        }
        else {
            await navigator.clipboard.writeText(url);
        }
    }
    async function translate() {
        const res = await fetch(`${TranslateHost}/translate`, {
            method: "POST",
            body: JSON.stringify({
                q: ev.content,
                source: "auto",
                target: lang.split("-")[0],
            }),
            headers: { "Content-Type": "application/json" },
        });
        if (res.ok) {
            const result = await res.json();
            if (typeof props.onTranslated === "function" && result) {
                props.onTranslated({
                    text: result.translatedText,
                    fromLanguage: langNames.of(result.detectedLanguage.language),
                    confidence: result.detectedLanguage.confidence,
                });
            }
        }
    }
    async function copyId() {
        const link = encodeTLV(NostrPrefix.Event, ev.id, ev.relays);
        await navigator.clipboard.writeText(link);
    }
    async function pin(id) {
        if (publisher) {
            const es = [...pinned.item, id];
            const ev = await publisher.noteList(es, Lists.Pinned);
            System.BroadcastEvent(ev);
            setPinned(login, es, ev.created_at * 1000);
        }
    }
    async function bookmark(id) {
        if (publisher) {
            const es = [...bookmarked.item, id];
            const ev = await publisher.noteList(es, Lists.Bookmarked);
            System.BroadcastEvent(ev);
            setBookmarked(login, es, ev.created_at * 1000);
        }
    }
    async function copyEvent() {
        await navigator.clipboard.writeText(JSON.stringify(ev, undefined, "  "));
    }
    function menuItems() {
        return (_jsxs(_Fragment, { children: [_jsx("div", { className: "close-menu-container", children: _jsx(MenuItem, { children: _jsx("div", { className: "close-menu" }) }) }), _jsxs(MenuItem, { onClick: () => setShowReactions(true), children: [_jsx(Icon, { name: "heart" }), _jsx(FormattedMessage, { ...messages.Reactions })] }), _jsxs(MenuItem, { onClick: () => share(), children: [_jsx(Icon, { name: "share" }), _jsx(FormattedMessage, { ...messages.Share })] }), !pinned.item.includes(ev.id) && (_jsxs(MenuItem, { onClick: () => pin(ev.id), children: [_jsx(Icon, { name: "pin" }), _jsx(FormattedMessage, { ...messages.Pin })] })), !bookmarked.item.includes(ev.id) && (_jsxs(MenuItem, { onClick: () => bookmark(ev.id), children: [_jsx(Icon, { name: "bookmark" }), _jsx(FormattedMessage, { ...messages.Bookmark })] })), _jsxs(MenuItem, { onClick: () => copyId(), children: [_jsx(Icon, { name: "copy" }), _jsx(FormattedMessage, { ...messages.CopyID })] }), _jsxs(MenuItem, { onClick: () => mute(ev.pubkey), children: [_jsx(Icon, { name: "mute" }), _jsx(FormattedMessage, { ...messages.Mute })] }), prefs.enableReactions && (_jsxs(MenuItem, { onClick: () => react("-"), children: [_jsx(Icon, { name: "dislike" }), _jsx(FormattedMessage, { ...messages.DislikeAction })] })), ev.pubkey === publicKey && (_jsxs(MenuItem, { onClick: handleReBroadcastButtonClick, children: [_jsx(Icon, { name: "relay" }), _jsx(FormattedMessage, { ...messages.ReBroadcast })] })), ev.pubkey !== publicKey && (_jsxs(MenuItem, { onClick: () => block(ev.pubkey), children: [_jsx(Icon, { name: "block" }), _jsx(FormattedMessage, { ...messages.Block })] })), _jsxs(MenuItem, { onClick: () => translate(), children: [_jsx(Icon, { name: "translate" }), _jsx(FormattedMessage, { ...messages.TranslateTo, values: { lang: langNames.of(lang.split("-")[0]) } })] }), prefs.showDebugMenus && (_jsxs(MenuItem, { onClick: () => copyEvent(), children: [_jsx(Icon, { name: "json" }), _jsx(FormattedMessage, { ...messages.CopyJSON })] })), isMine && (_jsxs(MenuItem, { onClick: () => deleteEvent(), children: [_jsx(Icon, { name: "trash", className: "red" }), _jsx(FormattedMessage, { ...messages.Delete })] }))] }));
    }
    const handleReplyButtonClick = () => {
        if (replyTo?.id !== ev.id) {
            dispatch(reset());
        }
        dispatch(setReplyTo(ev));
        dispatch(setShow(!showNoteCreatorModal));
    };
    const handleReBroadcastButtonClick = () => {
        if (reBroadcastNote?.id !== ev.id) {
            dispatch(resetReBroadcast());
        }
        dispatch(setReBroadcastNote(ev));
        dispatch(setReBroadcastShow(!showReBroadcastModal));
    };
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "footer", children: [_jsxs("div", { className: "footer-reactions", children: [tipButton(), reactionIcons(), repostIcon(), _jsx("div", { className: `reaction-pill ${showNoteCreatorModal ? "reacted" : ""}`, onClick: handleReplyButtonClick, children: _jsx(Icon, { name: "reply", size: 17 }) }), _jsx(Menu, { menuButton: _jsx("div", { className: "reaction-pill", children: _jsx(Icon, { name: "dots", size: 15 }) }), menuClassName: "ctx-menu", children: menuItems() })] }), willRenderNoteCreator && _jsx(NoteCreator, {}), willRenderReBroadcast && _jsx(ReBroadcaster, {}), _jsx(Reactions, { show: showReactions, setShow: setShowReactions, positive: positive, negative: negative, reposts: reposts, zaps: zaps }), _jsx(SendSats, { lnurl: getLNURL(), onClose: () => setTip(false), show: tip, author: author?.pubkey, target: getTargetName(), note: ev.id, allocatePool: true })] }), _jsx("div", { className: "zaps-container", children: _jsx(ZapsSummary, { zaps: zaps }) })] }));
}
